/**
 * PÁGINA PRINCIPAL — HTS (`/` e `/founding`)
 * --------------------------------------------------------------------
 * Até 2026-09-19 eram duas páginas: `/` era a lista de espera e `/founding` era o checkout. A
 * pré-venda abriu em 17/09 e quem chegava pelo orgânico caía na lista de espera, sem ver onde
 * comprar. Agora as duas URLs mostram a mesma página: a narrativa da lista de espera (copy de
 * 2026-09-14) com o checkout de verdade no lugar do formulário de e-mail. `/founding` continua
 * existindo porque foi divulgada em e-mail, influencer e anúncio.
 *
 * DOIS ESTADOS, trocados sozinhos pela API (`useFoundingState`):
 *   - aberta   → checkout + contador de vagas real;
 *   - esgotada → fila do próximo lote (formulário de e-mail + WhatsApp).
 *
 * Preço, lote e vagas vêm SEMPRE da API. O único valor fixo é o preço público (R$230).
 *
 * ORDEM: narrativa (001–005, posicionamento em narrative.tsx) → preço ancorado (006) → oferta com
 * checkout → FAQ (007) → arremate. O "o que está incluso" virou o capítulo 004 da narrativa.
 * O FAQ fica DEPOIS da oferta de propósito (dono, 2026-09-14): quem já se convenceu não precisa atravessar quinze perguntas pra achar onde comprar.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useSmoothScroll } from "@/hooks/use-smooth-scroll";
import { CustomCursor, Reveal, Chapter, StatsRow, Footer, AnchorTable, loteOrdinalLabel } from "@/components/site/shared";
import { NarrativeChapters } from "@/components/site/narrative";
import { WaitlistForm } from "@/components/site/waitlist-form";
import { CardParceiro } from "@/components/site/card-parceiro";
import { FaixaParceiros } from "@/components/site/faixa-parceiros";
import {
  ATIVACAO_PREVISTA,
  PRECO_PUBLICO,
  CheckoutForm,
  SlotsBar,
  formatLoteLabel,
  formatPrecoAtual,
  useFoundingState,
} from "@/components/site/founding";
import type { FoundingRemaining } from "@/lib/api";
import "@/styles/landing-v2.css";
import "@/styles/hts-extra.css";

/** No site, vídeo mora em `/videos/...`. No preview publicado como artifact a página não fica na
 *  raiz e caminho absoluto não resolve — daí o relativo só ali. */
const PREVIEW = Boolean(import.meta.env.VITE_PREVIEW_PAGE);
const asset = (p: string) => (PREVIEW ? p.replace(/^\//, "") : p);

const scrollToId = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

/** Quatro links na barra: seis viram sopa de letrinha. */
const NAV_LINKS = [
  { id: "ch-produto", label: "001 — O que é" },
  { id: "ch-ambiente", label: "002 — Ambiente" },
  { id: "ch-dentro", label: "004 — Recursos" },
  { id: "ch-faq", label: "007 — Dúvidas" },
];

// ============================================================================
// TOPBAR
// ============================================================================
function Topbar({ ctaLabel, onCTA }: { ctaLabel: string; onCTA: () => void }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`lv2-topbar ${scrolled ? "is-scrolled" : ""}`}>
      <a href="/" className="lv2-topbar-logo">
        <span className="hts-wordmark">HTS</span>
      </a>
      <nav className="lv2-topbar-nav">
        {NAV_LINKS.map((l) => (
          <button key={l.id} className="nav-link" onClick={() => scrollToId(l.id)}>{l.label}</button>
        ))}
        <button onClick={onCTA} className="cta">{ctaLabel}</button>
      </nav>
    </header>
  );
}

// ============================================================================
// HERO
// ============================================================================
/**
 * Headline e lead curtos DE PROPÓSITO (dono, 2026-09-14): em monitor grande, texto mais longo não
 * cabia na dobra e o título subia por cima da topbar. O vídeo é o produto rodando de verdade, não
 * mockup: numa página cujo argumento é "isto existe e roda todo dia", imagem inventada atrapalha.
 */
function HeroSection({ remaining, esgotada, onCTA }: { remaining: FoundingRemaining | null; esgotada: boolean; onCTA: () => void }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const content = contentRef.current;
    if (!wrapper || !content) return;
    const onScroll = () => {
      const rect = wrapper.getBoundingClientRect();
      const max = wrapper.offsetHeight - window.innerHeight;
      const progress = Math.max(0, Math.min(1, -rect.top / Math.max(1, max)));
      content.style.opacity = String(Math.max(0, 1 - progress * 1.4));
      content.style.transform = `translateY(${progress * -40}px)`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const lote = formatLoteLabel(remaining);
  const preco = formatPrecoAtual(remaining);
  const proximo = loteOrdinalLabel((remaining?.loteNumber ?? 1) + 1);

  return (
    <div ref={wrapperRef} className="lv2-hero-scroll">
      <section className="lv2-hero">
        <div
          className="lv2-hero-static"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(255,87,34,0.12) 0%, #050505 60%)",
          }}
        />
        <video className="hts-hero-video" src={asset("/videos/hero-plataforma.mp4")} autoPlay loop muted playsInline />
        <div className="hts-hero-fade" />
        <div className="lv2-hero-overlay" />

        <div ref={contentRef} className="lv2-hero-content">
          <div className="lv2-hero-meta">
            <div>
              <span>HTS — HIGH TRADING SYSTEM</span>
              <span>{esgotada ? `${lote} · Encerrado` : `${lote} · Pré-venda aberta`}</span>
            </div>
            <div style={{ textAlign: "right" }}>
              <span className="live">
                {esgotada ? "LOTE ESGOTADO" : remaining ? `${remaining.remaining} VAGAS RESTANTES` : "VAGAS LIMITADAS"}
              </span>
              <span>São Paulo · Brasil</span>
            </div>
          </div>

          <h1 className="lv2-display hts-hero-h1">
            O amador vê o movimento.
            <br />
            <span className="lv2-outlined">O profissional vê a informação.</span>
          </h1>

          <p style={{ maxWidth: 560, color: "#bdbdbd", fontSize: 16, lineHeight: 1.65 }}>
            Terminal profissional para o trader de B3 que conecta execução, juros, macro, notícia e
            performance no mesmo fluxo de decisão.
            <br />
            <em style={{ color: "#8a8a8a", fontStyle: "normal", fontSize: 13, display: "inline-block", marginTop: 12 }}>
              Não é informação privilegiada. É contexto, expectativa e precificação, antes da decisão.
            </em>
          </p>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={onCTA} className="hts-hero-cta" data-cursor-hover>
              {esgotada ? `Entrar na fila do ${proximo}` : remaining ? `Garantir minha vaga — ${preco}/mês` : "Garantir minha vaga"}
              <ArrowUpRight size={18} />
            </button>
            <button onClick={() => scrollToId("ch-dentro")} className="hts-hero-cta is-ghost" data-cursor-hover>
              Ver o que existe dentro da HTS
            </button>
          </div>
        </div>

        <div className="lv2-hero-scrollhint">Role para continuar</div>
      </section>
    </div>
  );
}

// ============================================================================
// 007 — O PREÇO (ancoragem contra o que o operador paga hoje)
// ============================================================================
function PriceChapter({ remaining }: { remaining: FoundingRemaining | null }) {
  const preco = formatPrecoAtual(remaining);
  const lote = formatLoteLabel(remaining);
  return (
    <Chapter id="ch-preco" number="006" eyebrow="O Preço" title={<>{preco}/mês,<br /><span className="lv2-outlined">travado por 12 meses.</span></>}>
      <div style={{ gridColumn: "1 / -1" }}>
        <AnchorTable precoAtual={preco} loteLabel={lote} />
        <p style={{ maxWidth: 760, margin: "40px auto 0", fontSize: 16, color: "#b8b8b8", lineHeight: 1.7, textAlign: "center" }}>
          O preço público da HTS é {PRECO_PUBLICO}/mês — mais barato que a assinatura de plataforma
          isolada que a maior parte dos operadores paga hoje. O {lote} entra a {preco} e mantém esse
          valor pelos 12 primeiros meses de assinatura ativa.
        </p>
      </div>
    </Chapter>
  );
}

// ============================================================================
// OFERTA — lote aberto: condição, cobrança e checkout
// ============================================================================
/** Bloco jurídico: "acesso ao mercado" é linguagem de intermediário, e a HTS não é um. Sem esta
 *  linha a página pode ser lida como oferta de corretagem (revisado pelo jurídico, 2026-09-05). */
function LegalNote() {
  return (
    <div className="hts-legal-note" style={{ maxWidth: 1180, margin: "28px auto 0" }}>
      A HTS é uma plataforma de análise e operação. Não é corretora, não intermedia ordens e
      não custodia recursos — sua conta e sua custódia continuam onde estão. Operações em
      mercado envolvem risco.
    </div>
  );
}

function OfertaAberta({ remaining }: { remaining: FoundingRemaining | null }) {
  const preco = formatPrecoAtual(remaining);
  const lote = formatLoteLabel(remaining);
  return (
    <section id="hts-cta" className="hts-offer">
      <Reveal>
        <div className="hts-offer-box">
          <div>
            <div className="hts-feat-label" style={{ color: "var(--accent)" }}>Founding Access · {lote}</div>
            <h2 className="hts-offer-head">
              {lote} aberto.
              <br />
              <span className="lv2-outlined">Você paga hoje. O relógio começa na ativação.</span>
            </h2>
            <p className="hts-offer-lead">
              O pagamento de hoje é o seu primeiro ciclo de 30 dias, e ele{" "}
              <strong style={{ color: "#fff", fontWeight: 500 }}>só começa a contar no dia em que sua conta for ativada</strong>.
              As ativações começam em {ATIVACAO_PREVISTA}, em ondas. Até lá, nenhuma cobrança nova.
              Desistiu antes da ativação? Devolvemos o valor integral, sem burocracia.
            </p>
            <p className="hts-offer-kicker">
              No lançamento: dado de mercado real e execução em simulador, pra você aprender a
              plataforma e montar processo sem risco.
            </p>
          </div>

          <div className="hts-offer-card">
            <div className="hts-offer-label">Condição Founding</div>
            <div className="hts-offer-old">Preço público: {PRECO_PUBLICO}/mês</div>
            <div className="hts-offer-price"><span>R$</span>{preco.replace(/^R\$/, "")}</div>
            <div className="hts-offer-fine">por mês, travado pelos 12 primeiros meses de assinatura ativa.</div>
            <SlotsBar remaining={remaining} style={{ margin: "24px 0 0" }} />
          </div>
        </div>

        <CardParceiro />

        <div className="lv2-phases" style={{ maxWidth: 1180, margin: "40px auto 0" }}>
          <div className="lv2-phase" data-cursor-hover>
            <div className="num">01 · AGORA</div>
            <div className="name">Pagamento</div>
            <div className="desc">Pagamento confirmado. Vaga garantida, preço travado.</div>
          </div>
          <div className="lv2-phase" data-cursor-hover>
            <div className="num">02 · A PARTIR DE {ATIVACAO_PREVISTA}</div>
            <div className="name">Ativação</div>
            <div className="desc">Sua conta é ativada, o acesso ao terminal é liberado e o primeiro ciclo de 30 dias começa.</div>
          </div>
          <div className="lv2-phase" data-cursor-hover>
            <div className="num">03 · 12 MESES</div>
            <div className="name">Preço travado</div>
            <div className="desc">{preco}/mês mantido durante todo o período, sem reajuste.</div>
          </div>
        </div>
      </Reveal>

      <div className="hts-checkout-body" style={{ padding: "56px 0 0" }}>
        <CheckoutForm remaining={remaining} />
      </div>

      <LegalNote />
    </section>
  );
}

// ============================================================================
// OFERTA — lote esgotado: fila do próximo
// ============================================================================
function OfertaEsgotada({ remaining }: { remaining: FoundingRemaining | null }) {
  const preco = formatPrecoAtual(remaining);
  const lote = formatLoteLabel(remaining);
  // O próximo lote ainda não tem `currentLote` incrementado (só o admin ativando faz isso), então
  // ele é sempre `loteNumber + 1`, calculado aqui.
  const proximo = loteOrdinalLabel((remaining?.loteNumber ?? 1) + 1);
  return (
    <section id="hts-cta" className="hts-offer">
      <Reveal>
        <div className="hts-offer-box">
          <div>
            <div className="hts-feat-label" style={{ color: "var(--accent)" }}>{lote} · Encerrado</div>
            <h2 className="hts-offer-head">
              {remaining ? remaining.totalSeats : "As"} vagas
              <br />
              <span className="lv2-outlined">foram preenchidas.</span>
            </h2>
            <p className="hts-offer-lead">
              O {lote} fechou. O {proximo} abre em seguida, com número de vagas e condições ainda em
              definição — quem está na fila é avisado primeiro. O preço do {proximo} não será o
              mesmo {preco}.
            </p>
          </div>
          <div className="hts-offer-card">
            <div className="hts-offer-label">Fila do {proximo}</div>
            <div className="hts-offer-fine" style={{ margin: "10px 0 18px" }}>Grátis, sem compromisso. Você é avisado antes da abertura.</div>
            <WaitlistForm proximoLote={proximo} />
          </div>
        </div>
        <LegalNote />
      </Reveal>
    </section>
  );
}

// ============================================================================
// B2B — mesa proprietária e white label (2026-09-19)
// ============================================================================
/**
 * Mandato, fases, trilha, extrato e saque NÃO entram no plano de pessoa física: são do módulo
 * `mesa`, que o Founding Access não recebe (ver narrative.tsx). Em vez de sumir com o assunto, ele
 * vira o que é de verdade — um produto separado, white label, com time de ativação e vendas. Assim
 * quem chega procurando mesa encontra o caminho certo, e a página de venda PF segue só prometendo
 * o que o terminal entrega.
 */
function MesaSection() {
  return (
    <section id="ch-mesa" className="hts-offer" style={{ paddingTop: 0 }}>
      <Reveal>
        <div className="hts-offer-box">
          <div>
            <div className="hts-feat-label" style={{ color: "var(--accent)" }}>Para mesas e assessorias</div>
            <h2 className="hts-offer-head">
              Tem uma mesa?
              <br />
              <span className="lv2-outlined">A HTS roda com a sua marca.</span>
            </h2>
            <p className="hts-offer-lead">
              O mesmo terminal, com o módulo de mesa proprietária por cima: mandato de risco,
              limites e monitor de risco, progressão de fases, trilha de aprendizado, extrato e
              saque dos operadores, backoffice e War Room. Vendido à parte, com time de ativação.
            </p>
          </div>

          <div className="hts-offer-card">
            <div className="hts-offer-label">Módulo Mesa · White label</div>
            <div className="hts-offer-fine" style={{ margin: "10px 0 20px" }}>
              Cobrança por assento, marca e domínio próprios, e seus operadores no mesmo terminal
              que você já viu aqui.
            </div>
            <a href="mailto:hts@hightrading.com.br?subject=Mesa%20propriet%C3%A1ria%20e%20white%20label" className="lv2-cta-btn" data-cursor-hover>
              Falar com o time
              <ArrowUpRight size={18} />
            </a>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

// ============================================================================
// 008 — FAQ
// ============================================================================
function buildFaq(preco: string, lote: string) {
  return [
    { q: "Já tenho plataforma e gráfico. Por que a HTS?", a: "Porque a HTS não compete em ter mais um indicador. Ela conecta a execução com o que está formando o preço — juros, dólar global, política monetária, risco e notícia — e depois transforma cada operação em diário e métricas. Não é mais uma tela: é o processo de decisão inteiro no mesmo ambiente." },
    { q: "Tenho uma mesa proprietária (ou quero montar). Dá pra usar a HTS?", a: "Dá. A mesa é um módulo à parte, em white label: mandato, limites e monitor de risco, fases, trilha, extrato, saque e backoffice, com a sua marca e cobrança por assento. Isso não faz parte deste plano de pessoa física — fale com o time por hts@hightrading.com.br." },
    { q: `O que está incluso no ${preco}?`, a: "Tudo o que está no capítulo 004: juros e mercado brasileiro, macro e internacional, notícias com contexto, HT TV, performance, workspace e a base de trading. Dado da B3 e internacional em tempo real. Um plano só, sem módulo adicional e sem taxa de market data cobrada por fora." },
    { q: "Quanto custa depois?", a: `O preço público é ${PRECO_PUBLICO}/mês. Quem entra no ${lote} paga ${preco} travado pelos 12 primeiros meses de assinatura ativa. Depois disso, passa para o preço vigente na época.` },
    { q: "Quando eu começo a usar?", a: `As ativações começam em ${ATIVACAO_PREVISTA}, em ondas. Você é avisado por e-mail quando a sua for liberada.` },
    { q: "Eu pago hoje e o ciclo começa hoje?", a: "Não. O pagamento de hoje é o seu primeiro ciclo de 30 dias, e ele começa a contar no dia da sua ativação." },
    { q: "Vou ser cobrado de novo antes de ser ativado?", a: "Não. Nenhuma cobrança nova acontece antes da ativação da sua conta." },
    { q: "Posso pedir reembolso?", a: "Sim, integral e sem burocracia, enquanto sua conta não tiver sido ativada. Depois de ativada, o cancelamento segue as regras normais de assinatura, sem cobrança do ciclo seguinte." },
    { q: "“Contexto antes da decisão” é informação privilegiada?", a: "Não. É contexto público: calendário, expectativa de mercado, curva de juros, dólar, risco e política monetária — tudo o que já está disponível antes da decisão, organizado num lugar só em vez de espalhado em cinco ferramentas. A HTS também não dá call nem diz onde comprar ou vender." },
    { q: "A execução de ordens é real?", a: `No lançamento, dado de mercado real e execução em simulador. A execução com conta real depende da conclusão da homologação com a corretora parceira na B3 e com a exchange internacional, e será liberada primeiro para o ${lote}.` },
    { q: "Isso é uma corretora? Preciso trocar a minha?", a: "Não. A HTS é plataforma de análise e operação. Sua conta e sua custódia continuam onde estão." },
    { q: "Roda em Mac? E no celular?", a: "Windows, Mac e navegador. No celular funciona como webapp; aplicativos nas lojas estão no roadmap." },
    { q: "Preciso saber programar?", a: "Não. Todas as ferramentas vêm prontas, cada uma com vídeo de uso." },
    { q: "Quem está por trás?", a: "A HTS é desenvolvida e operada pela High Trading LTDA e roda em produção desde 2021, hoje com 535 operadores profissionais em estrutura institucional." },
    { q: "Quantas vagas ainda existem?", a: "O contador desta página mostra o número real, atualizado direto do banco — não é estimativa." },
  ];
}

function FAQSection({ remaining }: { remaining: FoundingRemaining | null }) {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const items = buildFaq(formatPrecoAtual(remaining), formatLoteLabel(remaining));
  return (
    <Chapter id="ch-faq" number="007" eyebrow="Dúvidas Frequentes" title={<>Antes de<br />garantir a vaga.</>}>
      <div className="lv2-chapter-body" style={{ display: "block" }}>
        <div className="lv2-faq-list">
          {items.map((item, i) => {
            const open = openIdx === i;
            return (
              <div key={item.q} className={`lv2-faq-item ${open ? "is-open" : ""}`}>
                <button type="button" className="lv2-faq-q" data-cursor-hover onClick={() => setOpenIdx(open ? null : i)}>
                  <span style={{ display: "flex", alignItems: "center", flex: 1 }}>
                    <span className="lv2-faq-q-num">{String(i + 1).padStart(2, "0")}</span>
                    <span>{item.q}</span>
                  </span>
                  <span className="lv2-faq-q-icon" aria-hidden="true" />
                </button>
                <div className="lv2-faq-a"><p style={{ margin: 0 }}>{item.a}</p></div>
              </div>
            );
          })}
        </div>
      </div>
    </Chapter>
  );
}

// ============================================================================
// PAGE
// ============================================================================
export default function HomePage() {
  useSmoothScroll();
  const { state, remaining } = useFoundingState();
  const esgotada = state === "esgotada";
  const preco = formatPrecoAtual(remaining);
  const lote = formatLoteLabel(remaining);
  const proximo = loteOrdinalLabel((remaining?.loteNumber ?? 1) + 1);

  // Aberta: vai direto ao formulário do cartão. Esgotada: à caixa da fila.
  const goToOffer = useCallback(() => scrollToId(esgotada ? "hts-cta" : "form"), [esgotada]);

  return (
    <div className="lv2 hts-compact-display">
      <CustomCursor />
      <Topbar ctaLabel={esgotada ? "Entrar na fila" : "Garantir vaga"} onCTA={goToOffer} />

      <HeroSection remaining={remaining} esgotada={esgotada} onCTA={goToOffer} />

      <FaixaParceiros />

      <NarrativeChapters />

      <StatsRow
        animate={false}
        items={
          esgotada
            ? [
                { value: remaining ? remaining.totalSeats : "—", label: `Vagas preenchidas no ${lote}` },
                { value: 535, label: "Operadores profissionais já usam" },
                { value: "2021", label: "Em produção desde" },
              ]
            : [
                { value: preco, label: "Por mês, travado por 12 meses" },
                { value: remaining ? remaining.remaining : "—", label: `Vagas restantes no ${lote}` },
                { value: "30 dias", label: "1º ciclo só começa na ativação" },
                { value: "Reembolso", label: "Integral e sem burocracia antes da ativação" },
              ]
        }
      />

      {!esgotada && <PriceChapter remaining={remaining} />}

      {esgotada ? <OfertaEsgotada remaining={remaining} /> : <OfertaAberta remaining={remaining} />}

      <MesaSection />

      <FAQSection remaining={remaining} />

      <section className="lv2-cta">
        <div className="lv2-cta-bg" />
        <Reveal>
          <div className="lv2-chapter-eyebrow" style={{ marginBottom: 32 }}>
            {esgotada ? `Fila do ${proximo}` : `${lote} · Founding Access`}
          </div>
          <h2 className="hts-closing-h2">
            {esgotada ? (
              <>O {lote} fechou.<br /><span className="lv2-outlined">O próximo avisa a fila primeiro.</span></>
            ) : (
              <>O {lote} paga {preco}.<br /><span className="lv2-outlined">Depois,</span> {PRECO_PUBLICO}.</>
            )}
          </h2>
          {!esgotada && <SlotsBar remaining={remaining} />}
          <div style={{ marginTop: 40 }}>
            <button onClick={goToOffer} className="lv2-cta-btn" data-cursor-hover>
              {esgotada ? "Entrar na fila" : "Garantir minha vaga"}
              <ArrowUpRight size={18} />
            </button>
          </div>
          <p style={{ maxWidth: 560, margin: "32px auto 0", color: "#7d7d7d", fontSize: 13, lineHeight: 1.6 }}>
            Dúvida antes? <a href="mailto:hts@hightrading.com.br" style={{ color: "#aaa" }}>hts@hightrading.com.br</a>
          </p>
        </Reveal>
      </section>

      <Footer navLinks={NAV_LINKS.map((l) => ({ href: `#${l.id}`, label: l.label.split("— ")[1] }))} />
    </div>
  );
}
